import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  ERROR_CODES,
  getTranslatedError,
} from 'src/components/authentication/errors';
import {
  CONSTANTS,
  getTranslatedText,
} from 'src/components/authentication/contants';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {
  TitleMedium,
  BodyLarge,
  BodyMedium,
  ButtonTextMedium,
  SubTitleMedium,
  ButtonTextSmall,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {AuthFlow} from 'src/components/authentication/auth-flow';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import classify from 'src/utils/classify';
import {
  verifyOtpForUser,
  performSignIn,
  resendOtp,
  resetMfaDevice,
} from 'src/action-creators/authentication';

import {
  selectUserLoginId,
  selectUserEmail,
  selectUserNewPhoneNumber,
  selectCurrentAgency,
  selectOtpVerificationError,
  selectUserPhoneNumber,
  selectResendOtpError,
  selectOtpLimitExceeded,
  selectMfaAuthChannel,
  selectShouldEnrollTotp,
  selectResetMfaDeviceError,
} from 'src/selectors/authentication';

import type {AuthSideBarComp} from 'src/types/authentication';
import css from './otp-page.css';
import maincss from 'src/components/authentication/main.css';


const OtpPage = (props: AuthSideBarComp) => {
  const {makeKey, t} = useLabelTranslation();

  const dispatch = useDispatch();
  const {goToPage} = props;
  const [otpString, setOtpString] = React.useState<string>('');
  const [isVerifying, setIsVerifying] = React.useState<boolean>(false);
  const [isResendingOtp, setIsResendingOtp] = React.useState<boolean>(false);
  const [isResendSuccess, setIsResendSuccess] = React.useState<boolean>(false);

  const userEmail = useSelector(selectUserEmail);
  const loginId = useSelector(selectUserLoginId);
  const userPhone = useSelector(selectUserPhoneNumber);
  const newPhoneNumber = useSelector(selectUserNewPhoneNumber);
  const agency = useSelector(selectCurrentAgency);
  const apiOtpError = useSelector(selectOtpVerificationError);
  const {
    error_type: errorType,
    pending_attempts,
    error: otpError,
  } = apiOtpError || {};
  const isOtpLimitError = errorType === 'IncorrectOTP';

  // otp limit error text
  const otpLimitErrorText =
    'Invalid verification code : {{pending_attempts}} attempts left';

  const apiError = useSelector(selectResendOtpError);
  const otpLimitExceeded = useSelector(selectOtpLimitExceeded);
  const authChannel = useSelector(selectMfaAuthChannel);
  const authenticatorEnrollTotp = useSelector(selectShouldEnrollTotp);
  const isTotpChannelRegisterPhase =
    authChannel === 'TOTP' && authenticatorEnrollTotp && !userPhone;
  const resetPhoneError = useSelector(selectResetMfaDeviceError);

  const showResetPhoneNumber =
    (authChannel === 'SMS' || authChannel === 'TOTP') && userPhone;
  const validateOtp = async () => {
    if (otpLimitExceeded) {
      return;
    }
    setIsVerifying(true);
    setIsResendSuccess(false);
    const apiData = await dispatch(
      verifyOtpForUser(
        otpString,
        userEmail,
        loginId,
        newPhoneNumber,
        authChannel === 'EMAIL' ? 'EMAIL' : 'SMS',
        isTotpChannelRegisterPhase ? false : true,
      ),
    );
    // @ts-expect-error - TS2774 - This condition will always return true since this function is always defined. Did you mean to call it instead?
    if (apiData) {
      if (isTotpChannelRegisterPhase) {
        goToPage(AuthFlow.TOTP.SIGN_IN, 'TOTP');
      } else {
        dispatch(performSignIn(agency));
      }
    } else {
      setIsVerifying(false);
    }
  };

  const handleResentOtp = async () => {
    if (otpLimitExceeded) {
      return;
    }

    if (isResendingOtp) {
      return;
    }

    setIsResendingOtp(true);
    const response = await dispatch(
      resendOtp(userEmail, loginId, newPhoneNumber),
    );

    // @ts-expect-error - TS2774 - This condition will always return true since this function is always defined. Did you mean to call it instead?
    if (response) {
      setIsResendSuccess(true);
    } else {
      setIsResendSuccess(false);
    }

    setIsResendingOtp(false);
  };

  const handleResetPhone = async () => {
    await dispatch(resetMfaDevice(userEmail));
    if (!resetPhoneError) {
      goToPage(AuthFlow.SMS_REGISTRATION.RESET_PHONE, authChannel ?? '');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateOtp();
    }
  };
  const {isExtension} = useEnvironmentContext();
  const hashedPhone =
    newPhoneNumber || userPhone
      ? ' ******' + (newPhoneNumber || userPhone)?.slice(-4)
      : '';
  const smsOtpPageSubtext = [
    getTranslatedText(t, CONSTANTS.OTP_PAGE_SUBTEXT),
    hashedPhone + '.',
    t('Enter_the_code_below', 'Enter the code below.'),
  ].join(' ');
  return (
    <div
      className={classify(css.otpPage, {
        [css.otpPageExt]: isExtension,
      })}
    >
      <div className={css.titleSection}>
        {isExtension ? (
          <SubTitleMedium
            className={classify(css.contentTitle, maincss.alignSelfCenter)}
          >
            {isTotpChannelRegisterPhase
              ? t('Register_phone_number', 'Register phone number')
              : getTranslatedText(t, CONSTANTS.OTP_PAGE_TITLE)}
          </SubTitleMedium>
        ) : (
          <TitleMedium className={css.contentTitle}>
            {isTotpChannelRegisterPhase
              ? t('Register_phone_number', 'Register phone number')
              : getTranslatedText(t, CONSTANTS.OTP_PAGE_TITLE)}
          </TitleMedium>
        )}
        {(authChannel === 'SMS' || (hashedPhone && authChannel === 'TOTP')) &&
          (isExtension ? (
            <BodyMedium
              className={classify(maincss.subText, maincss.textAlignCenter)}
            >
              {smsOtpPageSubtext}
            </BodyMedium>
          ) : (
            <BodyLarge className={maincss.subText}>
              {smsOtpPageSubtext}
            </BodyLarge>
          ))}
        {authChannel === 'EMAIL' &&
          (isExtension ? (
            <BodyMedium
              className={classify(maincss.subText, maincss.textAlignCenter)}
            >
              {getTranslatedText(t, CONSTANTS.EMAIL_OTP_PAGE_SUBTEXT)}
            </BodyMedium>
          ) : (
            <BodyLarge className={maincss.subText}>
              {getTranslatedText(t, CONSTANTS.EMAIL_OTP_PAGE_SUBTEXT)}
            </BodyLarge>
          ))}
      </div>
      <div
        className={classify(css.otpSection, {
          [css.otpSectionExtension]: isExtension,
        })}
      >
        <div className={css.otpInput}>
          <Input
            label={t('Enter_verification_code', 'Enter verification code')}
            size="medium"
            type="tel"
            value={otpString}
            error={!!otpError}
            errorText={
              isOtpLimitError
                ? t(makeKey(otpLimitErrorText), {
                    pending_attempts,
                    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                    defaultValue: ERROR_CODES[otpError] || otpError,
                  })
                : t(
                    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                    makeKey(ERROR_CODES[otpError] || otpError),
                    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                    ERROR_CODES[otpError] || otpError,
                  )
            }
            onChange={(e) => setOtpString(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e)}
          />
        </div>
        <Button
          size="medium"
          type="primary"
          classNames={{
            wrapper: isExtension ? css.doneBtnExtension : css.doneBtn,
          }}
          onClick={validateOtp}
          disabled={isVerifying || !otpString.length || otpLimitExceeded}
        >
          {isVerifying ? t('Verifying', 'Verifying') : t('Verify', 'Verify')}
        </Button>
      </div>
      <div
        className={classify(css.resetSection, {
          [css.alignElements]: isExtension,
        })}
      >
        {isExtension ? (
          <BodySmall className={css.helpText}>
            {t('Didn\'t_get_the_code', 'Didn\'t get the code?')}
            <ButtonTextSmall
              color={
                isResendingOtp || otpLimitExceeded ? 'disabled' : 'clickable'
              }
              className={css.resendLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={handleResentOtp}
            >
              {isResendingOtp
                ? t('Resending', 'Resending')
                : t('Resend', 'Resend')}
            </ButtonTextSmall>
          </BodySmall>
        ) : (
          <BodyMedium className={css.helpText}>
            {t('Didn\'t_get_the_code', 'Didn\'t get the code?')}
            <ButtonTextMedium
              color={
                isResendingOtp || otpLimitExceeded ? 'disabled' : 'clickable'
              }
              className={css.resendLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={handleResentOtp}
            >
              {isResendingOtp
                ? t('Resending', 'Resending')
                : t('Resend', 'Resend')}
            </ButtonTextMedium>
          </BodyMedium>
        )}
        {showResetPhoneNumber &&
          (isExtension ? (
            <ButtonTextSmall
              color="clickable"
              className={css.resetPhoneLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={handleResetPhone}
            >
              {t('Reset', 'Reset')}
            </ButtonTextSmall>
          ) : (
            <ButtonTextMedium
              color="clickable"
              className={css.resetPhoneLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={handleResetPhone}
            >
              {t('Reset', 'Reset')}
            </ButtonTextMedium>
          ))}
      </div>
      {!isResendSuccess && !!apiError && (
        <InContextAlert
          semantic="danger"
          classNames={{wrapper: maincss.apiError}}
        >
          {t(
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
            makeKey(ERROR_CODES[apiError] || apiError),
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
            ERROR_CODES[apiError] || apiError,
          )}
        </InContextAlert>
      )}
      {resetPhoneError && (
        <InContextAlert
          semantic="danger"
          classNames={{wrapper: maincss.apiError}}
        >
          {t(makeKey(resetPhoneError), resetPhoneError)}
        </InContextAlert>
      )}
      {isResendSuccess && (
        <InContextAlert
          semantic="success"
          classNames={{wrapper: maincss.apiError}}
        >
          {getTranslatedText(t, CONSTANTS['RESEND_OTP_SUCCESS'])}
        </InContextAlert>
      )}
    </div>
  );
};

export default OtpPage;
